import React, { useEffect, useState } from 'react';
import { Post } from '@/core/types/postTypes';
import { useAppStore } from '@/core/store/useAppStore';
import { postsService } from './services/postsService';
import { SchedulePostModal } from './SchedulePostModal';

interface ScheduleErrorDisplayProps {
  post: Post;
}

interface ScheduleStatus {
  hasSchedule: boolean;
  status?: string;
  error?: {
    message: string;
    platformErrors?: string[];
  };
  platforms?: Array<{
    platform: string;
    status: string;
    errorMessage?: string;
    errorCode?: string;
  }>;
}

export const ScheduleErrorDisplay: React.FC<ScheduleErrorDisplayProps> = ({
  post,
}) => {
  const { selectedClient } = useAppStore();
  const [scheduleStatus, setScheduleStatus] = useState<ScheduleStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    if (!selectedClient) return;

    const fetchScheduleStatus = async () => {
      try {
        const status = await postsService.getScheduleStatus(selectedClient.id, post.id);
        setScheduleStatus(status);
      } catch (error) {
        console.error('Failed to fetch schedule status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchScheduleStatus();
  }, [post.id, selectedClient]);

  if (loading || !scheduleStatus?.hasSchedule) {
    return null;
  }

  // Show if status is error OR if completed but some platforms failed
  const hasPlatformErrors = scheduleStatus.platforms && scheduleStatus.platforms.some(
    p => p.status === 'failed'
  );

  const shouldShowErrors =
    scheduleStatus.status === 'error' ||
    (scheduleStatus.status === 'completed' && hasPlatformErrors);

  if (!shouldShowErrors) {
    return null;
  }

  if (!hasPlatformErrors && !scheduleStatus.error) {
    return null;
  }

  return (
    <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-sm">
      <div className="flex items-start gap-2">
        <svg
          className="w-5 h-5 text-red-500 shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <div className="flex-1">
          <div className="font-semibold text-red-800 mb-1">
            Publishing Failed
          </div>

          {/* General error message */}
          {scheduleStatus.error?.message && (
            <p className="text-sm text-red-700 mb-2">
              {scheduleStatus.error.message}
            </p>
          )}

          {/* Platform-specific errors */}
          {scheduleStatus.platforms && scheduleStatus.platforms.length > 0 && (
            <ul className="text-sm text-red-700 space-y-1 mb-3">
              {scheduleStatus.platforms.map((platform) => (
                <li key={platform.platform} className="flex items-start gap-2">
                  <span className="font-medium capitalize">{platform.platform}:</span>
                  <span>
                    {platform.status === 'failed'
                      ? platform.errorMessage || 'Publishing failed'
                      : platform.status === 'published'
                        ? '✓ Published successfully'
                        : platform.status}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Reschedule button */}
          <button
            onClick={() => setShowScheduleModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Fix Issues & Reschedule
          </button>
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && selectedClient && (
        <SchedulePostModal
          onClose={() => setShowScheduleModal(false)}
          post={post}
          clientId={selectedClient.id}
        />
      )}
    </div>
  );
};
